import "./App.scss";
import { AdjustmentData, BakeryItem, HandleShowFeedback } from "./types";
import { fetchProductPlanner, fetchPlanAdjustment } from "./backendServices";
import {
  getArea,
  convertTo12Hr,
  getAmPm,
  getTimeStamp,
  getOffset,
  isCurrentHour,
  getPLUPlan,
  getSentInHour
} from "./components/functions";
import React, { useEffect, useState } from "react";
import Joyride, { CallBackProps, STATUS } from "react-joyride";
import { joyrideStyles, joyrideStepsWaves } from "./components/helpers-joyride";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faQuestion } from "@fortawesome/pro-solid-svg-icons";

interface WavePlannerProps {
  storeName: string | null;
  isFOH: boolean;
  bakeTableState: BakeryItem[];
  setBakeTableState: (items: BakeryItem[]) => void;
  handleShowForcastEdit: HandleShowFeedback;
  refreshKey: number;
  handleShowHome: () => void;
}

const WavePlanner: React.FC<WavePlannerProps> = ({
  storeName,
  isFOH,
  bakeTableState,
  setBakeTableState,
  handleShowForcastEdit,
  refreshKey,
  handleShowHome
}) => {
  const [joyrideRun, setJoyrideRun] = useState<boolean>(false);
  const [orderingBy, setOrderingBy] = useState<keyof BakeryItem>("waves_order");
  const [productPlanner, setProductPlanner] = useState<any>([]);
  const [wavesExist, setWavesExist] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [adjustments, setAdjustments] = useState<any>([]);

  const [currentHour, setCurrentHour] = useState(() => {
    const now = new Date();
    return now.getHours();
  });

  const getAdjustment = (PLU: number, hour: number) => {
    const adjustedProduct = adjustments.find((item: AdjustmentData) => {
      return item.ProductId === PLU;
    });
    return adjustedProduct ? adjustedProduct[`H${hour}`] : -1;
  };

  const getPlannerTotal = (PLU: number) => {
    let total = 0;
    Array.from({ length: 19 - 6 }, (item, i) => 6 + i).forEach((i) => {
      const adjustment = getAdjustment(PLU, i);
      total +=
        adjustment != null && adjustment > -1
          ? adjustment
          : getPLUPlan(PLU, productPlanner)?.[`H${i}`];
    });
    return total;
  };

  const handleOrderBy = (type: keyof BakeryItem) => {
    orderBy(type);
    window.dataLayer.push({
      event: "Reorder by",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      value: type,
      time: getTimeStamp()
    });
  };

  const orderBy = (type: keyof BakeryItem) => {
    let bakeTableStateReOrdered;

    if (type == "already_made") {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) => {
        const aValue = a[type];
        const bValue = b[type];
        return bValue - aValue;
      });
    } else if (type == "start_hour") {
      const now = new Date();
      const currentHour = now.getHours();
      const end = "end_hour";
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) =>
        ("" + a["temp"]).localeCompare("" + b["temp"])
      );
      bakeTableStateReOrdered = [...bakeTableStateReOrdered].sort((a, b) => {
        if (a[end] <= currentHour && b[end] > currentHour) {
          return 1;
        } else if (a[end] > currentHour && b[end] <= currentHour) {
          return -1;
        } else {
          if (a[type] === b[type]) {
            return a[end] - b[end];
          }
          return a[type] - b[type];
        }
      });
    } else if (type == "waves_order") {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) => {
        const aValue = a[type] ?? 9999;
        const bValue = b[type] ?? 9999;
        return aValue - bValue;
      });
    } else if (type == "product_name" || type == "product_category") {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) =>
        ("" + a[type]).localeCompare("" + b[type])
      );
    } else {
      bakeTableStateReOrdered = [...bakeTableState].sort((a, b) => {
        const aValue = a[type] ?? 0;
        const bValue = b[type] ?? 0;
        return bValue - aValue;
      });
    }

    setBakeTableState(bakeTableStateReOrdered);
    setOrderingBy(type);
  };

  useEffect(() => {
    fetchPlanAdjustment()
      .then((adjustData) => {
        setAdjustments(adjustData);
        fetchProductPlanner()
          .then((productPlannerData) => {
            setProductPlanner(productPlannerData);
            const productPlannerDataReordered = [...productPlannerData].sort(
              (a, b) => {
                const priorityKeys = [
                  "H6",
                  "H7",
                  "H8",
                  "H9",
                  "H10",
                  "H11",
                  "H12",
                  "H13",
                  "H14",
                  "H15",
                  "H16",
                  "H17",
                  "H18"
                ];
                for (let key of priorityKeys) {
                  const aValue = a[key];
                  const bValue = b[key];

                  if (aValue !== bValue) {
                    return bValue - aValue;
                  }
                }
                return 0;
              }
            );

            const hours = Array.from({ length: 13 }, (_, i) => `P${6 + i}`);

            const countWaves = (waves: any[]) => {
              let count = 0;
              waves.forEach((wave) => {
                wave ? count++ : null;
              });
              count > 0 && !wavesExist ? setWavesExist(true) : null;
              return count;
            };

            const bakeTableStateWaved = bakeTableState;
            productPlannerDataReordered.forEach((item, index) => {
              const filteredItem = bakeTableStateWaved.filter(
                (stateItem: BakeryItem) => {
                  return stateItem["PLU"] === item["ProductId"];
                }
              )[0];
              if (filteredItem) {
                filteredItem["waves_order"] = index;
                for (let i = 6; i <= 18; i++) {
                  (filteredItem as { [key: string]: any })[`P${i}`] =
                    item[`H${i}`];
                }

                const waveCompareArray = hours.map(
                  (hour) => (filteredItem as { [key: string]: any })[hour] ?? 0
                );

                const waveRanks = (() => {
                  const hourQuantities = hours.map((hour) => ({
                    hour,
                    quantity:
                      (filteredItem as { [key: string]: any })[hour] ?? 0
                  }));

                  hourQuantities.sort((a, b) => b.quantity - a.quantity);

                  const ranks: Record<string, number> = {};
                  let currentRank = 1;

                  for (let i = 0; i < hourQuantities.length; i++) {
                    const { hour, quantity } = hourQuantities[i];

                    if (quantity === 0) {
                      ranks[hour] = 0;
                    } else if (
                      i > 0 &&
                      quantity === hourQuantities[i - 1].quantity
                    ) {
                      ranks[hour] = currentRank;
                    } else {
                      currentRank = i + 1;
                      ranks[hour] = currentRank;
                    }
                  }

                  return ranks;
                })();

                filteredItem["wave_ranks"] = waveRanks;
                filteredItem["rec_waves_count"] = countWaves(waveCompareArray);
              } else {
              }
            });

            setBakeTableState(bakeTableStateWaved);
            orderBy(orderingBy);
            setLoaded(true);
          })
          .catch((error) => {
            console.error(error);
            setLoaded(true);
          });
      })
      .catch((error) => {
        console.error(error);
        setLoaded(true);
      });

    const checkHour = () => {
      const now = new Date();
      if (now.getHours() != currentHour) {
        setCurrentHour(now.getHours());
      }
      setTimeout(checkHour, 60000);
    };

    checkHour();

    orderBy(orderingBy);
  }, [refreshKey]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    window.dataLayer.push({
      event: "Joyride Metric Support",
      value: status,
      step: data.index.toString(),
      type: type,
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });

    if (finishedStatuses.includes(status)) {
      setJoyrideRun(false);
    }
  };

  return (
    <header className="App-header">
      <div className="header-bar">
        <h4>Hourly Planner</h4>
      </div>
      <Joyride
        steps={joyrideStepsWaves}
        styles={joyrideStyles}
        run={joyrideRun}
        showProgress={false}
        continuous
        callback={handleJoyrideCallback}
        debug={false}
        disableScrolling
        showSkipButton
        hideCloseButton
        locale={{ skip: "Quit", last: "Finish" }}
      />

      <div className="scroll-table">
        <table className="bakery-table is-wide-view" cellSpacing="0">
          <thead>
            <tr className="bakery-table__header">
              <th
                className="bakery-table__name-column"
                onClick={() => handleOrderBy("product_name")}
              >
                <div className="h4">
                  Name
                  <FontAwesomeIcon
                    className={`jr-re-order ${
                      orderingBy == "product_name" ? "active" : ""
                    }`}
                    icon={faChevronDown}
                  />
                </div>
              </th>
              <th
                className="bakery-table__category-column hlpr-hide-tablet-narrow"
                onClick={() => handleOrderBy("product_category")}
              >
                <div className="h4">
                  Category
                  <FontAwesomeIcon
                    className={orderingBy == "product_category" ? "active" : ""}
                    icon={faChevronDown}
                  />
                </div>
              </th>

              <th className="h4 bakery-table__waves_column waves jr-waves">
                <div className="h4 waves__title">
                  Sent / forecast{" "}
                  <div className="waves-icons">
                    <FontAwesomeIcon
                      icon={faQuestion}
                      className="waves__help jr-waves-help"
                      onClick={() => {
                        setJoyrideRun(true);
                      }}
                    />
                    <FontAwesomeIcon
                      className={orderingBy == "waves_order" ? "active" : ""}
                      icon={faChevronDown}
                      onClick={() => handleOrderBy("waves_order")}
                    />
                  </div>
                </div>
                <div className="hlpr-flexbox waves__header p centered jr-waves-head">
                  {Array.from({ length: 19 - 6 }, (_, i) => 6 + i).map((i) => (
                    <div
                      key={i}
                      onClick={() => {
                        const key = `P${i}` as keyof BakeryItem;
                        handleOrderBy(key);
                      }}
                      className={`wave-${isCurrentHour(
                        i,
                        currentHour
                      )} wave-d--${getOffset(currentHour, i)}${
                        orderingBy == `P${i}` ? " active" : ""
                      }`}
                    >
                      <div>
                        {convertTo12Hr(i)}
                        <small>{getAmPm(i)}</small>
                      </div>
                    </div>
                  ))}
                  <div className="waves__totals jr-waves-head-totals">
                    Totals
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {wavesExist ? (
              bakeTableState.map((item, index) =>
                item.rec_waves_count ? (
                  <tr key={item.PLU} className="bakery-table__row">
                    <td className="jr-kpi-products">
                      <span className="h5 hlpr-cursor-pointer jr-kpi-products">
                        {item.product_name}
                      </span>
                    </td>
                    <td className="centered hlpr-hide-tablet-narrow">
                      {item.product_category.replace("-FOH", "")}
                    </td>
                    <td className="centered waves">
                      <div className="hlpr-flexbox waves__body jr-waves-body">
                        {Array.from({ length: 19 - 6 }, (item, i) => 6 + i).map(
                          (i) => (
                            <div
                              key={i}
                              onClick={() => {
                                handleShowForcastEdit(
                                  item.product_name,
                                  item.PLU.toString(),
                                  i.toString(),
                                  getPLUPlan(item.PLU, productPlanner)
                                    ? getPLUPlan(item.PLU, productPlanner)[
                                        `H${i}`
                                      ]
                                    : 0,
                                  getSentInHour(item, i) ?? 0
                                );
                              }}
                              className={`jr-slot wave-${isCurrentHour(
                                i,
                                currentHour
                              )} wave-d--${getOffset(currentHour, i)}`}
                            >
                              <div>
                                {getSentInHour(item, i) > 0
                                  ? `${getSentInHour(item, i)} /`
                                  : null}{" "}
                                {getAdjustment(item.PLU, i) != null &&
                                getAdjustment(item.PLU, i) > -1 ? (
                                  <span className="strong wave-adjustment">
                                    {getAdjustment(item.PLU, i)}
                                  </span>
                                ) : getPLUPlan(item.PLU, productPlanner)?.[
                                    `H${i}`
                                  ] != 0 ? (
                                  getPLUPlan(item.PLU, productPlanner)?.[
                                    `H${i}`
                                  ]
                                ) : null}
                              </div>
                            </div>
                          )
                        )}
                        <div className="waves__totals">
                          {item.already_made > 0
                            ? `${item.already_made} /`
                            : null}{" "}
                          {getPlannerTotal(item.PLU)}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null
              )
            ) : loaded ? (
              <tr className="bakery-table__row">
                <td>
                  <p className="strong">Ahhhhhhhhh wipeout</p>
                  <p>Sorry, looks like there's no surf today!</p>
                  <p>
                    Please ask your bakery manager for a production plan and if
                    you see this issue tomorrow contact KFP.
                  </p>
                  <p>
                    Return{" "}
                    <span
                      className="hlpr-text-underline"
                      onClick={handleShowHome}
                    >
                      home
                    </span>{" "}
                    to send to till.
                  </p>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </header>
  );
};

export default WavePlanner;
